var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { DexfSettingsUtility, TotalPriceCalculator } from '@inter-ikea-kompis/utilities';
import { CopyDesignCode } from '@inter-ikea-kompis/component-copy-design-code';
import { EcoFee, EcoFeeCard } from '@inter-ikea-kompis/component-eco-fee';
import { Hyperlink } from '@inter-ikea-kompis/component-hyperlink';
import { Image } from '@inter-ikea-kompis/component-image';
import { PriceModuleLayoutTypeEnum } from '@inter-ikea-kompis/component-price-module';
import { PriceModule } from '@inter-ikea-kompis/component-price-module';
import { Text } from '@inter-ikea-kompis/component-text';
import { AspectRatioBox as SkapaAspectRatio } from '@ingka/aspect-ratio-box-webc/passive.js';
import { Button as SkapaButton, IconButton as SkapaIconButton } from '@ingka/button-webc/passive.js';
import { Card as SkapaCard } from '@ingka/card-webc/passive.js';
import * as SkapaStyle from '@ingka/common-styles-webc';
import { checkmarkCircle, incorrect, informationCircle, registerIcon } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import { Image as SkapaImage } from '@ingka/image-webc/passive.js';
import { InlineMessage as SkapaInlineMessage } from '@ingka/inline-message-webc/passive.js';
import ConfigurationSummaryConfirmationCardLogInAndSaveStateEnum from '../../enums/ConfigurationSummaryConfirmationCardLogInAndSaveStateEnum.js';
import ConfigurationSummaryConfirmationCardStateEnum from '../../enums/ConfigurationSummaryConfirmationCardStateEnum.js';
import ConfigurationSummaryConfirmationCardTypeEnum from '../../enums/ConfigurationSummaryConfirmationCardTypeEnum.js';
import ConfirmationSummaryCopyDesignCodeStateEnum from '../../enums/ConfirmationSummaryCopyDesignCodeStateEnum.js';
import ConfigurationSummaryConfirmationCardTracker from '../../trackers/ConfigurationSummaryConfirmationCardTracker.js';
import ConfigurationSummaryCardUtility from '../../utilities/ConfigurationSummaryCardUtility.js';
import ConfigurationSummaryConfirmationCardStyle from './ConfigurationSummaryConfirmationCardStyle.js';
/**
 * ConfigurationSummaryConfirmationCard class.
 *
 * @event copyDesign When copy design is clicked.
 * @event confirmationCardStateChange When the state changes.
 * @event goToShoppingBag When goToShoppingBag is clicked
 * @event linkCardClick When the link card is clicked.
 * @event logInAndSaveDesign When save design is clicked.
 */
export default class ConfigurationSummaryConfirmationCard extends KompisElement {
    constructor() {
        super(...arguments);
        // VPC code. If not provided the "keep your design"-section will not show.
        this.vpcCode = null;
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Disables links. This attribute should be set to "true" when in a Kiosk.
        this.disableLinks = false;
        // Defines the state for add to bag button
        this.type = ConfigurationSummaryConfirmationCardTypeEnum.addToCartSuccess;
        // Show a warning about it includes unavailable products.
        this.addUnavailableProductsToList = false;
        // Defines the state for copy design code
        this.copyDesignState = ConfirmationSummaryCopyDesignCodeStateEnum.default;
        // The url for the screenshot.
        this.screenshotUrl = null;
        // The alt text for the screenshot. If not set, a default alt text will be used.
        this.screenshotAlt = null;
        // The name of the product.
        this.productName = null;
        // The current state of the component
        this.state = ConfigurationSummaryConfirmationCardStateEnum.default;
        // Displays link card when data and Översätta key showLinkCard are set. For image ratio guidelines, see Skapa Card. Hidden on kiosks when disableLinks is true.
        this.linkCard = null;
        // Used for automatic analytics. If null or not set, analytics is turned off.
        this.analytics = null;
        // Whether the user is logged in to IKEA or not.
        this.loggedIn = false;
        // Shows the save design button.
        this.showSaveDesign = false;
        // Defines the state for the log in and save button.
        this.logInAndSaveState = ConfigurationSummaryConfirmationCardLogInAndSaveStateEnum.default;
        this.tracker = null;
        this.dexfSettings = null;
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        // We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        return html `
			<style>
				${ConfigurationSummaryConfirmationCardStyle.getStyle({
            theme: this.theme,
            hasScreenshot: !!this.screenshotUrl,
            type: this.type
        })}
			</style>
			${this.getContentBasedOnState()}
		`;
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.disconnectTracker();
    }
    async connectedCallback() {
        registerIcon(incorrect);
        registerIcon(informationCircle);
        registerIcon(checkmarkCircle);
        this.customElements.define('skapa-aspect-ratio', SkapaAspectRatio);
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        this.customElements.define('skapa-icon', SkapaIcon);
        this.customElements.define('skapa-inline-message', SkapaInlineMessage);
        this.customElements.define('skapa-card', SkapaCard);
        this.customElements.define('kompis-text', Text);
        this.customElements.define('kompis-hyperlink', Hyperlink);
        this.customElements.define('kompis-copy-design-code', CopyDesignCode);
        this.customElements.define('kompis-price-module', PriceModule);
        this.customElements.define('kompis-eco-fee', EcoFee);
        this.customElements.define('kompis-image', Image);
        this.customElements.define('skapa-image', SkapaImage);
        this.customElements.define('kompis-eco-fee-card', EcoFeeCard);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        if (changedProperties.has('analytics')) {
            this.disconnectTracker();
            this.connectTracker();
        }
        return true;
    }
    hasTranslationsAndSettings() {
        return (!!this.translations && !!this.settings) || !!this.localizedInformation;
    }
    getSettings() {
        return this.dexfSettings;
    }
    getTranslations() {
        const translations = this.translations
            ? this.translations
            : this.localizedInformation?.translations;
        return translations;
    }
    async connectTracker() {
        if (this.analytics) {
            const reporter = this.analytics.__INTERNAL__reporter;
            if (reporter?.connected === false) {
                // Should use an existing reporter, but wait for it to be connected
                return;
            }
            // Use existing connected reporter, or the tracker should create a new one.
            const reporterId = reporter ? reporter.id : undefined;
            this.tracker = new ConfigurationSummaryConfirmationCardTracker({
                ...this.analytics,
                settings: this.getSettings(),
                reporterId
            });
            await this.tracker?.connect();
        }
    }
    disconnectTracker() {
        this.tracker?.disconnect();
        this.tracker = null;
    }
    getContentBasedOnState() {
        switch (this.state) {
            case ConfigurationSummaryConfirmationCardStateEnum.ecoFeeCard:
                return this.getEcoFeeCard();
            default:
                return html `
					${this.getTitle()}
					<div class="content">
						${this.getProductInformation()}${this.getGoToLink()}${this.getContent()}
					</div>
				`;
        }
    }
    getTitle() {
        return html `<h2 class="title ${SkapaStyle.Text.classnames({ heading: { m: true } })}">
			${ConfigurationSummaryCardUtility.getSheetBodyTitleText(this.getTranslations(), this.type)}
		</h2>`;
    }
    getEcoFeeCard() {
        const totalPrice = TotalPriceCalculator.calculate(this.shoppingProducts, this.getSettings().localisation.dateFormat);
        return html `
			<kompis-eco-fee-card
				.disableLinks="${this.disableLinks}"
				.totalPrice="${totalPrice}"
				.localizedInformation="${this.localizedInformation}"
				.settings="${this.settings}"
				.translations="${this.translations}"
				.theme="${this.theme}"
			></kompis-eco-fee-card>
		`;
    }
    getProductInformation() {
        if (this.type ===
            ConfigurationSummaryConfirmationCardTypeEnum.addUnavailabeToCartFailAndListSuccess) {
            return html `
				<p
					class="errorMessageProductInformation ${SkapaStyle.Text.classnames({
                body: { m: true }
            })}"
				>
					${this.getTranslations().ikeaGenericErrorTryAgain}
				</p>
			`;
        }
        if (this.type === ConfigurationSummaryConfirmationCardTypeEnum.saveVpcFail) {
            return null;
        }
        return html `
			<div class="productInformation">
				<kompis-image
					.src="${this.screenshotUrl}"
					.alt="${this.screenshotAlt ?? this.getTranslations().summaryPageScreenshotAltText}"
				>
				</kompis-image>
				<kompis-price-module
					.theme="${this.theme}"
					.shoppingProducts="${this.shoppingProducts}"
					.localizedInformation="${this.localizedInformation}"
					.translations="${this.translations}"
					.settings="${this.settings}"
					.productName="${this.productName}"
					.disableLinks="${this.disableLinks}"
					.layout="${PriceModuleLayoutTypeEnum.rowPrice}"
					.showRegularPriceExclTax="${true}"
					.showEcoFee="${true}"
					.showPriceExclTax="${true}"
					@modalOpen="${(event) => this.dispatchEvent(new CustomEvent('confirmationCardStateChange', {
            bubbles: true,
            detail: { selectedState: event.detail.visibleModal }
        }))}"
				>
					<slot></slot>
				</kompis-price-module>
			</div>
		`;
    }
    getGoToLink() {
        switch (this.type) {
            case ConfigurationSummaryConfirmationCardTypeEnum.saveVpcFail:
                return null;
        }
        const buttonVariant = this.type ===
            ConfigurationSummaryConfirmationCardTypeEnum.addUnavailabeToCartFailAndListSuccess
            ? 'primary'
            : 'emphasised';
        return html `
			<div class="goToLink">
				<skapa-button
					href="${this.getButtonLink()}"
					new-window
					fluid
					variant="${buttonVariant}"
					@click="${() => this.dispatchEvent(new CustomEvent('goToShoppingBag', {
            bubbles: true
        }))}"
				>
					${this.getButtonLabel()}
				</skapa-button>
			</div>

			${this.getInformationInlineMessage()} ${this.getErrorInlineMessage()}
		`;
    }
    getContent() {
        if (this.type === ConfigurationSummaryConfirmationCardTypeEnum.saveVpcFail) {
            return html `${this.getErrorInlineMessage()}`;
        }
        const saveDesignArea = this.getSaveDesignArea();
        const linkCard = this.getLinkCardArea();
        if (!saveDesignArea && !linkCard) {
            return null;
        }
        return html `
			${this.getLine()}
			<div class="saveDesignAreaAndLinkCardContainer">${saveDesignArea}${linkCard}</div>
		`;
    }
    getSaveDesignArea() {
        const saveDesign = this.getSaveDesign();
        if (saveDesign) {
            return saveDesign;
        }
        return this.showSaveDesign ? null : this.getVpcArea();
    }
    getVpcArea() {
        if (!this.vpcCode) {
            return null;
        }
        if (this.type === ConfigurationSummaryConfirmationCardTypeEnum.saveVpcFail) {
            return null;
        }
        return html `<div class="vpcArea">
			<h3 class="heading ${SkapaStyle.Text.classnames({ heading: { s: true } })}">
				${this.getTranslations().designCodeDescriptionTitle}
			</h3>
			${this.getVpcContent()}
			<p class="information ${SkapaStyle.Text.classnames({ body: { s: true } })}">
				${this.getTranslations().designCodeDescription}
			</p>
		</div> `;
    }
    getSaveDesign() {
        if (!this.showSaveDesign) {
            return null;
        }
        if (this.loggedIn &&
            this.logInAndSaveState === ConfigurationSummaryConfirmationCardLogInAndSaveStateEnum.default) {
            return null;
        }
        const informativeVariant = 'informative';
        const buttonVariant = 'secondary';
        return html `<div class="saveDesignContainer">
			<skapa-inline-message subtle variant="${informativeVariant}">
				<skapa-icon slot="message-icon" icon="information-circle"></skapa-icon>
				<span slot="body">${this.getTranslations().confirmationCardSaveDesignHint}</span>
			</skapa-inline-message>
			<skapa-button
				variant="${buttonVariant}"
				fluid
				?loading="${this.logInAndSaveState ===
            ConfigurationSummaryConfirmationCardLogInAndSaveStateEnum.loading}"
				loadingSrText="${this.getTranslations().loading}"
				@click="${this.onSaveClick.bind(this)}"
				>${this.getTranslations().save}</skapa-button
			>
		</div>`;
    }
    getLine() {
        return html `<div class="line"></div>`;
    }
    onSaveClick() {
        this.dispatchEvent(new CustomEvent('logInAndSaveDesign', {
            bubbles: true
        }));
    }
    getLinkCardArea() {
        if (!this.getSettings().kompis.localization.showLinkCard ||
            this.disableLinks ||
            !this.linkCard) {
            return null;
        }
        const { imageUrl, imageAltText, titleText, bodyTextLines, buttonText, linkUrl, meta } = this.linkCard;
        return html `
			<skapa-card
				url="${linkUrl}"
				target="_blank"
				fixed
				@click="${() => {
            this.dispatchEvent(new CustomEvent('linkCardClick', {
                bubbles: true,
                detail: meta
            }));
            this.tracker?.sendLinkCardClick(meta);
        }}"
				theme="default"
			>
				<skapa-aspect-ratio slot="media" ratio="16:9"
					><skapa-image><img alt="${imageAltText || ''}" src="${imageUrl}" /></skapa-image
				></skapa-aspect-ratio>
				<h2 slot="title">${titleText}</h2>
				<skapa-button slot="button" size="small" variant="secondary" decorative-only
					>${buttonText}</skapa-button
				>
				<p slot="body">
					${bodyTextLines.map((line, index) => index < bodyTextLines.length - 1 ? html `${line}<br />` : html `${line}`)}
				</p>
			</skapa-card>
		`;
    }
    getErrorInlineMessage() {
        let errorMessage = '';
        const { codeFailureText, addToShoppingCartUnavailableCheckOutError } = this.getTranslations();
        switch (this.type) {
            case ConfigurationSummaryConfirmationCardTypeEnum.saveVpcFail: {
                errorMessage = codeFailureText;
                break;
            }
            case ConfigurationSummaryConfirmationCardTypeEnum.addUnavailabeToCartSuccessAndListFail: {
                errorMessage = addToShoppingCartUnavailableCheckOutError;
                break;
            }
        }
        const negativeVariant = 'negative';
        return errorMessage
            ? html `
					<skapa-inline-message class="errorMessage" subtle variant="${negativeVariant}">
						<skapa-icon slot="message-icon" icon="incorrect"></skapa-icon>
						<span slot="body">${errorMessage}</span>
					</skapa-inline-message>
				`
            : null;
    }
    /**
     *
     * @returns Template.
     */
    getVpcContent() {
        return html `
			<kompis-copy-design-code
				.theme="${this.theme}"
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.settings="${this.settings}"
				.vpcCode="${this.vpcCode}"
				.state="${this.copyDesignState}"
				@copy="${this.onCopy.bind(this)}"
				@reset="${this.onReset.bind(this)}"
			>
			</kompis-copy-design-code>
		`;
    }
    /**
     * On copy design code.
     *
     * @param event Event - should either be a browser copy event or a custom one.
     */
    onCopy(event) {
        if ('detail' in event &&
            event.detail.state !== ConfirmationSummaryCopyDesignCodeStateEnum.success) {
            this.dispatchEvent(new CustomEvent('copyDesign', {
                bubbles: true,
                detail: {
                    copyDesignState: ConfirmationSummaryCopyDesignCodeStateEnum.default,
                    error: true
                }
            }));
        }
        else {
            this.dispatchEvent(new CustomEvent('copyDesign', {
                bubbles: true,
                detail: {
                    copyDesignState: ConfirmationSummaryCopyDesignCodeStateEnum.success,
                    error: false
                }
            }));
        }
    }
    /**
     * On copy design code.
     */
    onReset() {
        this.dispatchEvent(new CustomEvent('copyDesign', {
            bubbles: true,
            detail: {
                copyDesignState: ConfirmationSummaryCopyDesignCodeStateEnum.default,
                error: false
            }
        }));
    }
    getInformationInlineMessage() {
        if (!this.addUnavailableProductsToList ||
            this.type ===
                ConfigurationSummaryConfirmationCardTypeEnum.addUnavailabeToCartSuccessAndListFail) {
            return null;
        }
        const informativeVariant = 'informative';
        return html `
			<skapa-inline-message subtle class="informationMessage" variant="${informativeVariant}">
				<skapa-icon slot="message-icon" icon="information-circle"></skapa-icon>
				<span slot="body">${this.getInformationMessageContent()}</span>
			</skapa-inline-message>
		`;
    }
    getInformationMessageContent() {
        let list = [];
        const { addToShoppingCartUnavailableCheckOutConfirmation } = this.getTranslations();
        if (addToShoppingCartUnavailableCheckOutConfirmation.includes('[[')) {
            list = addToShoppingCartUnavailableCheckOutConfirmation.split('[[shopping_list]]');
        }
        else if (addToShoppingCartUnavailableCheckOutConfirmation.includes('{{')) {
            list = addToShoppingCartUnavailableCheckOutConfirmation.split('{{shopping_list}}');
        }
        if (list.length !== 2) {
            return addToShoppingCartUnavailableCheckOutConfirmation;
        }
        return html `
			${list[0]}
			<kompis-hyperlink
				.theme="${this.theme}"
				.target="${'_blank'}"
				.underline="${true}"
				.text="${this.getTranslations().addToShoppingListName}"
				.href="${this.getSettings().kompis.urls.shoppingListLink}"
			></kompis-hyperlink
			>${list[1]}
		`;
    }
    getButtonLink() {
        if (this.type === ConfigurationSummaryConfirmationCardTypeEnum.addToCartSuccess ||
            this.type ===
                ConfigurationSummaryConfirmationCardTypeEnum.addUnavailabeToCartAndListSuccess ||
            this.type ===
                ConfigurationSummaryConfirmationCardTypeEnum.addUnavailabeToCartSuccessAndListFail) {
            return this.getSettings().kompis.urls.shoppingCartLink;
        }
        return this.getSettings().kompis.urls.shoppingListLink;
    }
    getButtonLabel() {
        if (this.type === ConfigurationSummaryConfirmationCardTypeEnum.addToCartSuccess ||
            this.type ===
                ConfigurationSummaryConfirmationCardTypeEnum.addUnavailabeToCartAndListSuccess ||
            this.type ===
                ConfigurationSummaryConfirmationCardTypeEnum.addUnavailabeToCartSuccessAndListFail) {
            return this.getTranslations().shoppingBagLinkTo;
        }
        return this.getTranslations().shoppingListLinkTo;
    }
}
__decorate([
    property({ type: String })
], ConfigurationSummaryConfirmationCard.prototype, "vpcCode", void 0);
__decorate([
    property({ type: Object, required: true })
], ConfigurationSummaryConfirmationCard.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], ConfigurationSummaryConfirmationCard.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], ConfigurationSummaryConfirmationCard.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], ConfigurationSummaryConfirmationCard.prototype, "settings", void 0);
__decorate([
    property({ type: Boolean })
], ConfigurationSummaryConfirmationCard.prototype, "disableLinks", void 0);
__decorate([
    property({ type: String })
], ConfigurationSummaryConfirmationCard.prototype, "type", void 0);
__decorate([
    property({ type: Boolean })
], ConfigurationSummaryConfirmationCard.prototype, "addUnavailableProductsToList", void 0);
__decorate([
    property({ type: String })
], ConfigurationSummaryConfirmationCard.prototype, "copyDesignState", void 0);
__decorate([
    property({ type: String })
], ConfigurationSummaryConfirmationCard.prototype, "screenshotUrl", void 0);
__decorate([
    property({ type: String })
], ConfigurationSummaryConfirmationCard.prototype, "screenshotAlt", void 0);
__decorate([
    property({ type: String })
], ConfigurationSummaryConfirmationCard.prototype, "productName", void 0);
__decorate([
    property({ type: Object, required: true })
], ConfigurationSummaryConfirmationCard.prototype, "shoppingProducts", void 0);
__decorate([
    property({ type: String })
], ConfigurationSummaryConfirmationCard.prototype, "state", void 0);
__decorate([
    property({ type: Object })
], ConfigurationSummaryConfirmationCard.prototype, "linkCard", void 0);
__decorate([
    property({ type: Object })
], ConfigurationSummaryConfirmationCard.prototype, "analytics", void 0);
__decorate([
    property({ type: Boolean })
], ConfigurationSummaryConfirmationCard.prototype, "loggedIn", void 0);
__decorate([
    property({ type: Boolean })
], ConfigurationSummaryConfirmationCard.prototype, "showSaveDesign", void 0);
__decorate([
    property({ type: String })
], ConfigurationSummaryConfirmationCard.prototype, "logInAndSaveState", void 0);
//# sourceMappingURL=ConfigurationSummaryConfirmationCard.js.map